import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { List, X } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { BUSINESS } from "../constants/site";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/about", label: "About" },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <>
      <header
        data-testid="site-navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-[background,padding] duration-500 ${
          scrolled ? "glass-nav py-3" : "py-5 bg-transparent"
        }`}
      >
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
            <img src={BUSINESS.logo} alt="Creative Designs Services" className={`w-auto transition-all duration-500 group-hover:scale-105 ${scrolled ? "h-14 sm:h-16" : "h-16 sm:h-20"}`} />
            <span className="hidden md:block leading-tight">
              <span className="font-display text-xl text-[color:var(--text)] tracking-wide">Creative Designs</span>
              <span className="block font-mono-x text-[10px] tracking-[0.3em] uppercase text-[color:var(--gold)]">Services · Bahamas</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-10">
            {LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={`link-underline text-sm tracking-wide transition-colors duration-300 ${
                  pathname === l.to ? "text-[color:var(--gold)]" : "text-[color:var(--text-2)] hover:text-[color:var(--text)]"
                }`}
              >
                {l.label}
              </Link>
            ))}
            <Link
              to="/contact"
              data-testid="nav-cta"
              className="rounded-full border border-[color:var(--gold)] px-6 py-2.5 text-sm tracking-wide text-[color:var(--gold)] transition-all duration-500 hover:bg-[color:var(--gold)] hover:text-[#0b0710]"
            >
              Request a Quote
            </Link>
          </nav>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden text-[color:var(--gold)] p-1"
            aria-label="Menu"
          >
            {open ? <X size={26} /> : <List size={26} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 lg:hidden bg-[color:var(--bg)]/97 backdrop-blur-xl flex flex-col items-center justify-center gap-8"
          >
            {LINKS.map((l, i) => (
              <motion.div
                key={l.to}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 * i }}
              >
                <Link
                  to={l.to}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                  className="font-display text-4xl text-[color:var(--text)] hover:text-[color:var(--gold)] transition-colors"
                >
                  {l.label}
                </Link>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
